import { getCompany } from '@simplywallst/services';
import { QueryFunctionContext, useQuery } from 'react-query';

type IncludeParameters = (
  | 'info'
  | 'score'
  | 'score.snowflake'
  | 'analysis.extended.raw_data'
  | 'analysis.extended.raw_data.insider_transactions'
)[];

export function getQueryKey(
  lookupValue: string,
  includeParameters: IncludeParameters = [
    'info',
    'score',
    'score.snowflake',
    'analysis.extended.raw_data',
    'analysis.extended.raw_data.insider_transactions',
  ]
): ['company', string, string] {
  return ['company', lookupValue, includeParameters.join(',')];
}

export async function queryFn(
  context: QueryFunctionContext<ReturnType<typeof getQueryKey>>
) {
  const { 1: lookupValue, 2: includeParameters } = context.queryKey;
  const response = await getCompany({
    lookupVal: lookupValue,
    payload: {
      include: includeParameters,
      version: '2.0',
    },
  });

  if (typeof response.data.data === 'undefined') {
    throw new Error('`data` response is undefined');
  }
  return response.data;
}

/**
 *
 * Custom hook for fetching company data
 *
 * @param lookupValue
 * @param includeParameters
 *
 * lookupValue accepts the following options
 *
 * * canonincal url of a company
 * * company id
 * * unique symbol
 *
 * For `includeParameters` please refer to API rest docs
 *
 */
export function useCompany(
  lookupValue: string,
  includeParameters: IncludeParameters = [
    'info',
    'score',
    'score.snowflake',
    'analysis.extended.raw_data',
    'analysis.extended.raw_data.insider_transactions',
  ]
) {
  const { data, status } = useQuery(
    getQueryKey(lookupValue, includeParameters),
    queryFn,
    {
      staleTime: 21600,
      retry: 3,
      enabled: lookupValue !== '',
    }
  );

  if (status === 'success') {
    return { data, status };
  }
  return { status };
}
